/**
 * createMarketingCmsClient — thin client SDK that fetches public-read
 * marketing-CMS endpoints. The blog page components consume this client to
 * render index + detail views (and to fetch on client-side SPA navigation,
 * where there is no SSR seed to skip the fetch).
 *
 * Default basePath is the PUBLIC ingress path `/api/v1/marketing-cms`: this
 * SDK runs in the browser, so its calls traverse the public reverse-proxy,
 * which only forwards `^/api` (and `^/blog`) to the API container and strips
 * the `/api` prefix before it. A bare `/v1/marketing-cms` falls through to the
 * SPA shell (HTTP 200 `text/html`) and `response.json()` then throws — which
 * surfaced as the "We couldn't load the blog" banner on SPA nav to /blog.
 * Callers targeting the API directly (custom `baseUrl`, bypassing the ingress)
 * pass an explicit `basePath: '/v1/marketing-cms'`.
 *
 * Transport: an injectable fetch. Production wiring passes
 * `globalThis.fetch`; tests pass a stub.
 *
 * @module @puffin-core/marketing-shell/blog/createMarketingCmsClient
 */

// 🔴 The SUBPATH, never the barrel. `envelope.js` imports NOTHING; the barrel re-exports twelve
// modules, one of which pulls in **axios** — and this client is reachable from nine PRERENDERED
// marketing entries, so a barrel import here is a bundle defect, not a style preference. The same
// mistake was caught on `createFetchSeam` by `marketingEntryBarrelFreedom` (PERF-FECSS-002);
// ⚠️ that contract scans APP ENTRY files, so it would NOT have caught this one — a package-internal
// import inside the marketing graph is its blind spot. Queued as `Q-BARREL-PKGSCOPE`.
import { readEnvelopeData } from '@puffin-core/api-client/envelope';

const DEFAULT_BASE_PATH = '/api/v1/marketing-cms';

/**
 * @typedef {Object} CreateMarketingCmsClientConfig
 * @property {string} appKey
 * @property {Function} fetch                Fetch impl (DI).
 * @property {string} [baseUrl='']           e.g. 'https://api.parcelpuffin.com'. Empty → relative.
 * @property {string} [basePath='/api/v1/marketing-cms']
 */

/**
 * @typedef {Object} MarketingCmsClient
 * @property {(opts?: {limit, offset, locale}) => Promise<Array>} listBlogPosts
 * @property {(opts: {slug, locale}) => Promise<{post, meta}|null>} resolveBlogPost
 * @property {(opts?: {locale}) => Promise<Array>} listBlogCategories
 * @property {(opts?: {locale}) => Promise<Array>} listBlogTags
 */

/**
 * @param {CreateMarketingCmsClientConfig} config
 * @returns {MarketingCmsClient}
 */
export default function createMarketingCmsClient(config) {
  validateConfig(config);
  const { appKey, fetch, baseUrl = '', basePath = DEFAULT_BASE_PATH } = config;
  const root = `${stripTrailingSlash(baseUrl)}${basePath}/${encodeURIComponent(appKey)}`;

  async function request(path, query) {
    let url = `${root}${path}`;
    // Every call site passes a query object (possibly with all-undefined
    // values); an empty query simply yields no params (the loop is a no-op),
    // so no separate "if (query)" guard is needed.
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      params.set(k, String(v));
    }
    const s = params.toString();
    if (s) url += `?${s}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (response.status === 404) return { _missing: true };
    if (!response.ok) {
      const err = new Error(`marketingCmsClient: HTTP ${response.status} from ${url}`);
      err.status = response.status;
      throw err;
    }
    const body = await response.json();
    return body;
  }

  return {
    appKey,
    // 🔴 RES-CLIENT-001 (2026-08-26). The reads below were `body.data || body` — the
    // provenance sniffer, on the PUBLIC blog + CMS path of nine marketing sites.
    // The fallback arm was the dangerous half: it handed back the ENVELOPE as if it were the
    // payload, so `data?.rows` was `undefined` and the list methods returned `[]`. A blog index
    // that could not read its response rendered as a blog with no posts. Nothing logged, nothing
    // threw, and the page looked finished.
    //
    // ✅ `Q-APITIER-READER` CLOSED 2026-08-30 (owner-ruled "take it now"). The reads now go through
    // `readEnvelopeData`, which THROWS on a body that is not a canonical envelope instead of
    // returning `undefined` for one — so an unreadable response reaches an error boundary rather
    // than being rendered as an empty blog.
    //
    // 🔴 And the `Array.isArray(data?.rows) ? data.rows : []` tails are DELETED, not re-stubbed
    // (T7-RECIPE §3). Measured at the route, not assumed: `/blog/{posts,categories,tags}` each
    // answer `sendSuccess(res, { rows })` UNCONDITIONALLY
    // (`marketing-cms/lib/routes/createMarketingCmsPublicReadRouter.js`). The arm could
    // therefore fire on exactly ONE input — a body this client failed to read — and that is the
    // input it must not answer for.
    //
    // `_missing` stays: it is the 404 sentinel `request()` sets, checked BEFORE the reader, and a
    // genuine "no such post" is a fact the server stated rather than one this client inferred.
    //
    // LIFE-SHORTLISTED-001 Q-LS-12 — `resolvePage` (the page builder's read) is gone with the
    // builder; its public route is no longer mounted.
    async listBlogPosts({ limit, offset, locale } = {}) {
      const body = await request('/blog/posts', { limit, offset, locale });
      if (!body || body._missing) return [];
      return readEnvelopeData(body).rows;
    },
    async resolveBlogPost({ slug, locale }) {
      if (typeof slug !== 'string' || !slug) return null;
      const body = await request(`/blog/posts/${encodeURIComponent(slug)}`, { locale });
      if (!body || body._missing) return null;
      return readEnvelopeData(body);
    },
    async listBlogCategories({ locale } = {}) {
      const body = await request('/blog/categories', { locale });
      if (!body || body._missing) return [];
      return readEnvelopeData(body).rows;
    },
    async listBlogTags({ locale } = {}) {
      const body = await request('/blog/tags', { locale });
      if (!body || body._missing) return [];
      return readEnvelopeData(body).rows;
    },
  };
}

function stripTrailingSlash(url) {
  return typeof url === 'string' && url.endsWith('/') ? url.slice(0, -1) : url;
}

function validateConfig(config) {
  if (!config || typeof config !== 'object') {
    throw new TypeError('createMarketingCmsClient: config object is required');
  }
  if (typeof config.appKey !== 'string' || !config.appKey) {
    throw new TypeError('createMarketingCmsClient: config.appKey is required');
  }
  if (typeof config.fetch !== 'function') {
    throw new TypeError('createMarketingCmsClient: config.fetch (function) is required');
  }
}
